import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  loading?: boolean;
  children: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  // One primary per screen. Everything else is visually subordinate.
  primary: 'bg-accent text-text-on-accent hover:bg-accent-hover',
  secondary: 'bg-surface-2 text-text hover:bg-surface-3',
  ghost: 'bg-transparent text-text-secondary hover:bg-surface-2 hover:text-text',
  danger: 'bg-danger text-text-on-accent hover:opacity-90',
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'secondary', loading = false, disabled, className, children, ...rest },
  ref,
) {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      // aria-busy rather than swapping the label to "Loading..." -- screen
      // readers announce the state without the button losing its name.
      aria-busy={loading || undefined}
      disabled={isDisabled}
      className={cn(
        'pressable inline-flex min-h-touch items-center justify-center gap-2',
        'rounded px-4 text-body font-medium',
        'transition-colors duration-press',
        // Disabled must read as disabled: dimmed, and the cursor says so.
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading && (
        <span
          aria-hidden
          className="size-icon shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
});
